import { colorOrToken, Icons, ShapeColor, tokens } from '@cast/design-system';
import {
  HPAScalingPolicyType,
  PolicyAutoscalerMode,
  WorkloadEventType,
  WorkloadManager,
  WorkloadRecommendationStatusTypeEnum,
} from '@cast/types';

import { OptimizationType } from 'features/workload-optimization';

export type ErrorStatusType = 'ERROR';
export type LowConfidenceStatusType = 'LOW_CONFIDENCE';

export const AUTOSCALER_BEHAVIOR_POLICY_TYPE_NAMES: Record<
  HPAScalingPolicyType,
  string
> = {
  [HPAScalingPolicyType.PERCENT_SCALING_POLICY]: 'Percent',
  [HPAScalingPolicyType.PODS_SCALING_POLICY]: 'Pods',
  [HPAScalingPolicyType.HPA_SCALING_POLICY_TYPE_UNSPECIFIED]: 'Unspecified',
};

export const minimumWoopAgentVersionForHpa = 'v0.10.1';

export const minimumWoopAgentVersionForDeferredMode = 'v0.5.0';

export const minimumWoopAgentVersionForSingleResource = 'v0.23.1';

export const minimumWoopAgentVersionForZeroDowntime = 'v0.35.3';
export const minimumWoopAgentVersionForRestartPodsOneByOne = 'v0.57.0';
export const minimumWoopAgentVersionForAutoInstrument = 'v1.0.3';

export const AUTOSCALER_WORKLOAD_NAME = 'castai-workload-autoscaler';

export const scalingPolicyDuplicateFromUrlKey = 'duplicate-from';

export const maxLookbackPeriodSeconds = 604_800; // 7 days
export const minMemoryConstraintValueInGib = 0.009765625; // 10 Mib

export const minCpuConstraintValue = 0.01;
export const autoscalerModeDisplayName: Record<PolicyAutoscalerMode, string> = {
  [PolicyAutoscalerMode.IMMEDIATE]: 'Immediately',
  [PolicyAutoscalerMode.DEFERRED]: 'Deferred',
  [PolicyAutoscalerMode.UNKNOWN]: 'UNKNOWN',
};

type WorkloadManagerType =
  (typeof WorkloadManager)[keyof typeof WorkloadManager];

export const configuredByLabels: Record<WorkloadManagerType, string> = {
  [WorkloadManager.ANNOTATIONS]: 'Annotations',
  [WorkloadManager.API]: 'API',
};

export const configuredByLabelOptions = Object.entries(configuredByLabels).map(
  ([key, title]) => ({
    title,
    value: key as WorkloadManagerType,
  })
);

export const ERROR_STATUS_TYPE: ErrorStatusType = 'ERROR';
export const LOW_CONFIDENCE_STATUS_TYPE: LowConfidenceStatusType =
  'LOW_CONFIDENCE';

export const STATUS_DISPLAY_NAME_MAP = {
  [WorkloadRecommendationStatusTypeEnum.APPLIED]: 'Applied',
  [WorkloadRecommendationStatusTypeEnum.STOPPED]: 'Stopped',
  [ERROR_STATUS_TYPE]: 'Failed',
  [LOW_CONFIDENCE_STATUS_TYPE]: 'Low confidence',
} as const;

export const STATUS_BADGE_COLOR_MAP = {
  [WorkloadRecommendationStatusTypeEnum.APPLIED]: {
    bgcolor: colorOrToken('green.50', tokens.theme.bg.status.successSubtle),
    color: colorOrToken('green.600', tokens.theme.fg.status.success),
  },
  [WorkloadRecommendationStatusTypeEnum.STOPPED]: {
    bgcolor: colorOrToken('red.50', tokens.theme.bg.status.errorSubtle),
    color: colorOrToken('red.500', tokens.theme.fg.status.error),
  },
  [ERROR_STATUS_TYPE]: {
    bgcolor: colorOrToken('red.100', tokens.theme.bg.status.errorSubtle),
    color: colorOrToken('red.500', tokens.theme.fg.status.error),
  },
  [LOW_CONFIDENCE_STATUS_TYPE]: {
    bgcolor: colorOrToken('yellow.50', tokens.theme.bg.status.warningSubtle),
    color: colorOrToken('yellow.900', tokens.theme.fg.status.warning),
  },
} as const;

export const STATUS_ICON_MAP = {
  [WorkloadRecommendationStatusTypeEnum.APPLIED]: Icons.CheckCircle,
  [WorkloadRecommendationStatusTypeEnum.STOPPED]: Icons.StopCircle,
  [ERROR_STATUS_TYPE]: Icons.Warning,
  [LOW_CONFIDENCE_STATUS_TYPE]: Icons.WarningCircle,
} as const;

export const eventTypeConfigMap: Partial<
  Record<
    WorkloadEventType,
    {
      name: string;
      color: ShapeColor;
      contrastTextColor: string;
    }
  >
> = {
  [WorkloadEventType.RECOMMENDED_REQUESTS_CHANGED]: {
    name: 'Recommendation generated',
    color: 'grey.600',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.RECOMMENDED_POD_COUNT_CHANGED]: {
    name: 'Pod count recommendation updated',
    color: 'indigo.100',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.CONFIGURATION_CHANGEDV2]: {
    name: 'Configuration changed',
    color: 'indigo.500',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.SURGE]: {
    name: 'Resource surge',
    color: 'yellow.300',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.CPU_PRESSURE]: {
    name: 'CPU stall detected',
    color: 'yellow.600',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.CPU_PRESSURE_RESOLVED]: {
    name: 'CPU stall resolved',
    color: 'green.500',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.FAILED_HELM_TEST_HOOK]: {
    name: 'Failed Helm test',
    color: 'red.200',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.STARTUP_FAILURE]: {
    name: 'Startup Failure',
    color: 'red.300',
    contrastTextColor: colorOrToken(
      '#fff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.OOM_KILL]: {
    name: 'OOMKill',
    color: 'red.500',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.SYSTEM_OVERRIDE_RESET]: {
    name: 'System Override: Reset',
    color: 'aqua.100',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.SYSTEM_OVERRIDE_TRIGGERED]: {
    name: 'System Override: Triggered',
    color: 'aqua.500',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.SCALING_POLICY_DELETED]: {
    name: 'Vertical scaling policy: Deleted',
    color: 'grey.300',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.SCALING_POLICY_CREATED]: {
    name: 'Vertical scaling policy: Created',
    color: 'green.300',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.SCALING_POLICY_ASSIGNED]: {
    name: 'Vertical scaling policy: Assigned',
    color: 'green.500',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.SCALING_POLICY_UPDATED]: {
    name: 'Vertical scaling policy: Edited',
    color: 'blue.200',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.SCALING_POLICY_ORDER_UPDATED]: {
    name: 'Vertical scaling policy: Order changed',
    color: 'blue.500',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.HPA_MAXED_OUT]: {
    name: 'HPA maxed out',
    color: 'red.400',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.HPA_ALMOST_MAXED_OUT]: {
    name: 'HPA almost maxed out',
    color: 'yellow.500',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
  [WorkloadEventType.UNBOUND_MEMORY_GROWTH]: {
    name: 'Unbound memory growth',
    color: 'red.300',
    contrastTextColor: colorOrToken(
      '#ffffff',
      tokens.theme.fg.structural.primaryOnInverse
    ),
  },
  [WorkloadEventType.NATIVE_VPA_STATE_CHANGED]: {
    name: 'Native VPA state changed',
    color: 'blue.300',
    contrastTextColor: colorOrToken(
      'grey.900',
      tokens.theme.fg.structural.primary
    ),
  },
};

export const OPTIMIZATION_TYPE_TO_SEPARATE_OPTIONS_MAP = {
  [OptimizationType.BOTH]: {
    vpaEnabled: true,
    hpaEnabled: true,
  },
  [OptimizationType.VERTICAL]: {
    vpaEnabled: true,
    hpaEnabled: false,
  },
  [OptimizationType.HORIZONTAL]: {
    vpaEnabled: false,
    hpaEnabled: true,
  },
  [OptimizationType.OFF]: {
    vpaEnabled: false,
    hpaEnabled: false,
  },
} as const;

export const ERROR_EVENTS: WorkloadEventType[] = [
  WorkloadEventType.OOM_KILL,
  WorkloadEventType.FAILED_HELM_TEST_HOOK,
  WorkloadEventType.STARTUP_FAILURE,
];

export const CAST_EVENTS: WorkloadEventType[] = [
  WorkloadEventType.SYSTEM_OVERRIDE_RESET,
  WorkloadEventType.SYSTEM_OVERRIDE_TRIGGERED,
];

export const EVENT_TYPES_WITHOUT_CONTAINERS: WorkloadEventType[] = [
  WorkloadEventType.RECOMMENDED_POD_COUNT_CHANGED,
  WorkloadEventType.CONFIGURATION_CHANGEDV2,
  WorkloadEventType.FAILED_HELM_TEST_HOOK,
  WorkloadEventType.SYSTEM_OVERRIDE_RESET,
  WorkloadEventType.SYSTEM_OVERRIDE_TRIGGERED,
];

export const OPTIMIZATION_TYPE_HINT_MAP: Record<
  OptimizationType,
  string | null
> = {
  [OptimizationType.BOTH]:
    'Both VPA and HPA will be enabled for comprehensive optimization.',
  [OptimizationType.VERTICAL]:
    'Vertical Pod Autoscaler will adjust CPU and memory requests based on usage.',
  [OptimizationType.HORIZONTAL]:
    'Horizontal Pod Autoscaler will adjust replica counts based on load.',
  [OptimizationType.OFF]: null,
};

export const OPTIMIZATION_TYPE_OPTIONS = [
  OptimizationType.OFF,
  OptimizationType.VERTICAL,
  OptimizationType.HORIZONTAL,
  OptimizationType.BOTH,
];

export const OPTIMIZATION_TYPE_HORIZONTAL_OPTIONS = [
  OptimizationType.HORIZONTAL,
  OptimizationType.BOTH,
];

export const OPTIMIZATION_TYPE_NON_HORIZONTAL_OPTIONS = [
  OptimizationType.OFF,
  OptimizationType.VERTICAL,
];

export const WORKLOAD_TABLE_SEARCH_URL_KEY = 'woop-list';
